/**
 * useAudioAnalyzer
 * React hook that attaches a Web Audio AnalyserNode to a MediaStream and
 * samples RMS + spectral centroid + F0 pitch every 100ms to compute three
 * signal metrics:
 *
 *   energy        (0–100): vocal loudness / amplitude (RMS-based)
 *   monotony      (0–100): tonal flatness via spectral centroid variance
 *   pitch_variance (Hz):   std dev of fundamental frequency (F0) across voiced
 *                          frames — < 15 Hz suggests monotone delivery;
 *                          null if fewer than 5 voiced frames were detected
 *
 * Usage:
 *   const { attachStream, stopAndGetMetrics, cleanup } = useAudioAnalyzer();
 *
 *   // After stream is available:
 *   attachStream(stream);
 *
 *   // When recording stops:
 *   const audioFeatures = stopAndGetMetrics(); // { energy, monotony, pitch_variance } | null
 *
 *   // On unmount:
 *   cleanup();
 *
 * Only works in browsers that support the Web Audio API. Gracefully returns
 * null on unsupported environments without throwing.
 */
import { useRef, useCallback } from 'react';

const FFT_SIZE = 2048;
const SAMPLE_INTERVAL_MS = 100;

// Minimum number of voiced frames required to report a meaningful pitch_variance.
// Fewer than this means the recording was too short or too quiet to measure.
const MIN_VOICED_FRAMES = 5;

// Minimum autocorrelation peak strength to classify a frame as voiced (speech).
// Below this threshold the frame is silence/noise and is excluded from F0 stats.
const VOICED_THRESHOLD = 0.3;

/**
 * Autocorrelation-based fundamental frequency (F0) estimator.
 * Uses the time-domain buffer already collected for RMS energy — no extra
 * Web Audio calls needed.
 *
 * Returns the estimated F0 in Hz, or null if the frame is unvoiced/silent.
 *
 * Algorithm: compute autocorrelation for lags covering the human voice range
 * (roughly 50–500 Hz), find the lag with the highest normalised peak, convert
 * to Hz. O(bufLen * voiceRange) per frame — fast enough for 100 ms intervals.
 */
function estimateF0(timeBuf, sampleRate) {
  const minLag = Math.floor(sampleRate / 500); // 500 Hz upper limit
  const maxLag = Math.ceil(sampleRate / 50);   // 50 Hz lower limit

  // Normalise buffer energy to make the threshold independent of volume
  let energy = 0;
  for (let i = 0; i < timeBuf.length; i++) energy += timeBuf[i] * timeBuf[i];
  if (energy < 1e-6) return null; // silence

  let bestLag = -1;
  let bestCorr = -1;

  for (let lag = minLag; lag <= maxLag && lag < timeBuf.length; lag++) {
    let corr = 0;
    for (let i = 0; i < timeBuf.length - lag; i++) {
      corr += timeBuf[i] * timeBuf[i + lag];
    }
    // Normalise by zero-lag energy so peak is in [−1, 1]
    const normCorr = corr / energy;
    if (normCorr > bestCorr) {
      bestCorr = normCorr;
      bestLag = lag;
    }
  }

  if (bestCorr < VOICED_THRESHOLD) return null; // unvoiced frame
  return sampleRate / bestLag;
}

export default function useAudioAnalyzer() {
  const contextRef  = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef   = useRef(null);
  const intervalRef = useRef(null);
  const rmsRef      = useRef([]);
  const centroidRef = useRef([]);
  const pitchRef    = useRef([]);

  /**
   * Attach a MediaStream to the analyser and start sampling.
   * Safe to call multiple times — previous context is replaced.
   */
  const attachStream = useCallback((stream) => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return; // unsupported browser — graceful no-op

    // Reset any previous session
    clearInterval(intervalRef.current);
    sourceRef.current?.disconnect();
    contextRef.current?.close();

    const ctx      = new AudioCtx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    const source   = ctx.createMediaStreamSource(stream);
    source.connect(analyser); // analysis only — NOT connected to ctx.destination

    contextRef.current  = ctx;
    analyserRef.current = analyser;
    sourceRef.current   = source;
    rmsRef.current      = [];
    centroidRef.current = [];
    pitchRef.current    = [];

    const bufLen  = analyser.frequencyBinCount; // FFT_SIZE / 2 = 1024
    const timeBuf = new Float32Array(bufLen);
    const freqBuf = new Float32Array(bufLen);

    intervalRef.current = setInterval(() => {
      // ── RMS (energy) ──────────────────────────────────────────────────
      analyser.getFloatTimeDomainData(timeBuf);
      const sumSq = timeBuf.reduce((s, v) => s + v * v, 0);
      rmsRef.current.push(Math.sqrt(sumSq / bufLen));

      // ── Spectral centroid (monotony proxy) ────────────────────────────
      analyser.getFloatFrequencyData(freqBuf); // values in dB
      const binHz = ctx.sampleRate / FFT_SIZE;
      let weightedSum = 0;
      let magSum = 0;
      for (let i = 0; i < bufLen; i++) {
        const mag = Math.pow(10, freqBuf[i] / 20); // dB → linear magnitude
        weightedSum += i * binHz * mag;
        magSum += mag;
      }
      centroidRef.current.push(magSum > 0 ? weightedSum / magSum : 0);

      // ── F0 pitch via autocorrelation (true pitch variance) ────────────
      // timeBuf is already populated by getFloatTimeDomainData above.
      const f0 = estimateF0(timeBuf, ctx.sampleRate);
      if (f0 !== null) pitchRef.current.push(f0);
    }, SAMPLE_INTERVAL_MS);
  }, []);

  /**
   * Stop sampling, close the AudioContext, and return computed metrics.
   * Returns null if no samples were collected (e.g. stream was never attached).
   */
  const stopAndGetMetrics = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;

    sourceRef.current?.disconnect();
    contextRef.current?.close();
    contextRef.current  = null;
    analyserRef.current = null;
    sourceRef.current   = null;

    const rms   = rmsRef.current;
    const sc    = centroidRef.current;
    const pitch = pitchRef.current;

    if (rms.length === 0) return null;

    // ── Energy: average RMS scaled to 0-100 ───────────────────────────
    // 0.25 RMS ≈ loud conversational speech → maps to 100
    const avgRms = rms.reduce((a, b) => a + b, 0) / rms.length;
    const energy = Math.min(100, Math.max(0, Math.round(avgRms * 400)));

    // ── Monotony: inverted coefficient of variation of spectral centroids
    // Low CV → flat pitch → high monotony score
    // cv = 0   → monotony = 100 (completely flat)
    // cv ≥ 0.3 → monotony = 0   (expressive)
    const scMean = sc.reduce((a, b) => a + b, 0) / sc.length;
    const scStd  = Math.sqrt(sc.reduce((s, v) => s + (v - scMean) ** 2, 0) / sc.length);
    const cv     = scMean > 0 ? scStd / scMean : 0;
    const monotony = Math.min(100, Math.max(0, Math.round((1 - cv / 0.3) * 100)));

    // ── Pitch variance: std dev of F0 across voiced frames (Hz) ─────────
    // null when too few voiced frames to be meaningful (short/quiet recording)
    let pitch_variance = null;
    if (pitch.length >= MIN_VOICED_FRAMES) {
      const pitchMean = pitch.reduce((a, b) => a + b, 0) / pitch.length;
      const pitchStd = Math.sqrt(
        pitch.reduce((s, v) => s + (v - pitchMean) ** 2, 0) / pitch.length
      );
      pitch_variance = Math.round(pitchStd * 10) / 10; // one decimal place, in Hz
    }

    // Reset buffers
    rmsRef.current      = [];
    centroidRef.current = [];
    pitchRef.current    = [];

    return { energy, monotony, pitch_variance };
  }, []);

  /**
   * Emergency teardown — call on component unmount to prevent AudioContext leaks.
   */
  const cleanup = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    sourceRef.current?.disconnect();
    contextRef.current?.close();
    contextRef.current  = null;
    analyserRef.current = null;
    sourceRef.current   = null;
    rmsRef.current      = [];
    centroidRef.current = [];
    pitchRef.current    = [];
  }, []);

  return { attachStream, stopAndGetMetrics, cleanup };
}
